import { useMemo } from "react";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ProductCategory } from "@utils/constants";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";

export const useGridMenuOption = (
  productType: ProductCategory,
  priceMode: number,
  t: any,
  customSearchData: any,
  routeName?: string
) => {
  const { enableOrderCartQuote } = useUserInfo();
  const showSendQuote =
    enableOrderCartQuote() === true && priceMode === PriceModes.WHITE;

  return useMemo(() => {
    const dataObject = customSearchData?.[productType];
    if (!dataObject?.grid_view?.["sub-sections"]) return null;

    const bulkActionItem = dataObject.grid_view["sub-sections"]?.menu;
    if (!bulkActionItem) return null;

    const extractOptions = (section: any) =>
      Object.values(section?.options || {})
        .filter((option: any) => option.price_modes.includes(priceMode))
        .sort((a: any, b: any) => parseFloat(a?.order) - parseFloat(b?.order))
        .filter((option: any) => {
          return (
            !option.accepted_screens ||
            option.accepted_screens.includes(routeName)
          );
        })
        .map((option: any) => ({
          ...option,
          label: t(option?.label),
        }));

    return extractOptions(bulkActionItem).filter(
      (option: { name?: string }) => {
        if (
          option?.name === SearchResultsAndFavoritesEnum.BUY_NOW &&
          showSendQuote
        ) {
          return false;
        }
        if (
          option?.name === SearchResultsAndFavoritesEnum.SEND_QUOTE &&
          !showSendQuote
        ) {
          return false;
        }
        return true;
      }
    );
  }, [productType, priceMode, t, customSearchData, routeName, showSendQuote]);
};

export const useGridTotalPriceLabel = (
  productType: ProductCategory,
  priceMode: number,
  customSearchData: any
) => {
  const getGridMenuOption = () => {
    const dataObject = customSearchData?.[productType];
    if (!dataObject?.grid_view?.["sub-sections"]) return null;

    const bulkActionItem =
      dataObject.grid_view["sub-sections"]?.total_price_label;

    if (!bulkActionItem) return null;

    return bulkActionItem?.price_modes?.includes(priceMode)
      ? bulkActionItem
      : null;
  };

  return getGridMenuOption();
};

export const useHideSectionContainer = (
  productType: ProductCategory,
  customSearchData: any
) => {
  return useMemo(() => {
    const hideSection =
      customSearchData?.[productType]?.grid_view?.["sub-sections"]?.hideSection;

    return {
      hideSubtitleSection: hideSection?.sub_title_section ?? false,
      hidePriceSection: hideSection?.price_section ?? false,
      shortTitleLines: hideSection?.short_title_lines ?? null,
    };
  }, [productType, customSearchData]);
};

export const useGridTotalPrice = (
  productType: ProductCategory,
  priceMode: number,
  customSearchData: any
) => {
  const getGridMenuOption = () => {
    const dataObject = customSearchData?.[productType];
    if (!dataObject?.grid_view?.["sub-sections"]) return null;

    const bulkActionItem = dataObject.grid_view["sub-sections"]?.total_price;

    if (!bulkActionItem) return null;

    return bulkActionItem?.price_modes?.includes(priceMode)
      ? bulkActionItem
      : null;
  };

  return getGridMenuOption();
};

export const usePairIcon = (
  productType: ProductCategory,
  priceMode: number,
  customSearchData: any
) => {
  const getPairIcon = () => {
    const dataObject = customSearchData?.[productType];
    if (!dataObject?.grid_view?.["sub-sections"]) return null;

    const pairIcon = dataObject.grid_view["sub-sections"]?.pair_icon;

    if (!pairIcon) return false;

    return pairIcon?.price_modes?.includes(priceMode) ? true : false;
  };

  return getPairIcon();
};
